import { getTrackingProductId } from '@features/products/utils'
import { centAmountToAmountV2 } from '@lib/format-price'
import { graphql } from '@lib/graphql'
import { execute } from '@lib/graphql/execute'
import { CartLineItemsAddInput } from '@lib/graphql/graphql'
import { MutationConfig } from '@lib/react-query'
import { getCurrencyFromCountry } from '@lib/utils'
import { selectAlpha2Code, selectChannel } from '@redux/globalSlice'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mapEdgesToItems } from '@utils/map'
import {
  CurrencyCodes,
  getTrackingProductUrl,
  pushAddToBasketTrackingEvent,
} from '@utils/tracking-utils'
import { CHANNEL } from 'config'
import { useSelector } from 'react-redux'
import { cartsQueryKeys } from '../query-keys'
import {
  findOrCreateCart,
  getCartIdFromCookies,
  saveCartIdToCookie,
} from '../utils'

const cartAddLineItemsMutationDocument = graphql(`
  mutation CartAddLineItems($input: CartLineItemsAddInput!) {
    cartLineItemsAdd(input: $input) {
      cart {
        ...CartDetail
      }
      errors {
        code: __typename
      }
    }
  }
`)

export const cartAddLineItems = async (input: CartLineItemsAddInput) => {
  const result = await execute(cartAddLineItemsMutationDocument, { input })
  return result
}

export async function cartAddItem(
  {
    variantId,
    metadata,
  }: Omit<CartLineItemsAddInput['lineItems'][0], 'quantity'>,
  channel: CHANNEL,
  currency: string,
  countryCode: string,
) {
  const cart = await findOrCreateCart({
    cartId: getCartIdFromCookies(),
    channel,
    currency,
    countryCode,
  })

  if (!cart) {
    console.error('Ups, something went wrong')
    return
  }

  saveCartIdToCookie(cart.id)

  // add item to cart
  const addCartLineItemResponse = await cartAddLineItems({
    cartId: cart.id,
    lineItems: [{ variantId, quantity: 1, metadata }],
  })
  return {
    cart: addCartLineItemResponse.cartLineItemsAdd.cart,
    variantId: variantId,
  }
}

export const useCartAddItem = (
  mutationConfig?: MutationConfig<typeof cartAddItem>,
) => {
  const country = useSelector(selectAlpha2Code)
  const channel = useSelector(selectChannel)
  const queryClient = useQueryClient()
  const { onSuccess, ...restConfig } = mutationConfig || {}
  const currency = getCurrencyFromCountry(country)

  return useMutation({
    mutationFn: (
      lineItemInput: Omit<CartLineItemsAddInput, 'cartId'>['lineItems'][0],
    ) => cartAddItem(lineItemInput, channel, currency, country),
    onSuccess: (data, variables, context) => {
      const cart = data.cart
      const variantId = data.variantId
      queryClient.setQueryData(cartsQueryKeys.all, cart)

      const addedVariant = mapEdgesToItems(cart.lineItems)?.find(
        (item) => item.variant.id === variantId,
      )
      pushAddToBasketTrackingEvent({
        currencyCode: cart.currency as CurrencyCodes,
        products: [
          {
            id: getTrackingProductId(
              addedVariant.variant?.product.id,
              addedVariant.variant?.product?.metadata,
            ),
            name: addedVariant.variant?.product.name,
            variant: addedVariant.variant?.name,
            category: addedVariant.variant?.product?.tags
              ?.find((tag) => tag.startsWith('type_'))
              ?.split('_')[1]
              .replace(/-/g, ''),
            brand: addedVariant.variant?.product.vendor,
            image_url: addedVariant.variant?.image.src,
            url: getTrackingProductUrl(
              addedVariant.variant?.product?.slug,
              addedVariant.variant?.sku,
            ),
            quantity: 1,
            price: centAmountToAmountV2(
              addedVariant.unitPrice.discountedPrice
                ? addedVariant.unitPrice.discountedPrice.value
                : addedVariant.unitPrice.value,
            )?.toString(),
            dimension1: addedVariant.variant?.sku,
          },
        ],
        cart,
      })

      onSuccess?.(data, variables, context)
    },
    ...restConfig,
  })
}
